<script lang="ts" setup>
import ToggleSwitch from 'primevue/toggleswitch'
import type { StaffPermissions } from '~/types/staffPermissions'

const props = defineProps<{
  modelValue: StaffPermissions
}>()

const emit = defineEmits<{
  'update:modelValue': [value: StaffPermissions]
}>()

const { t } = useI18n()

function update<K extends keyof StaffPermissions>(key: K, value: StaffPermissions[K]) {
  emit('update:modelValue', { ...props.modelValue, [key]: value })
}
</script>

<template>
  <div class="space-y-6">
    <div>
      <h4 class="text-sm font-semibold text-gray-700 mb-3">
        {{ t('Shift & Finance') }}
      </h4>
      <div class="space-y-4">
        <div class="flex items-center gap-3">
          <ToggleSwitch
            :model-value="modelValue.can_view_shift_financials"
            input-id="can_view_shift_financials"
            @update:model-value="update('can_view_shift_financials', $event)"
          />
          <label
            for="can_view_shift_financials"
            class="text-sm font-medium cursor-pointer"
          >
            {{ t('View shift financials') }}
          </label>
        </div>
      </div>
    </div>

    <div>
      <h4 class="text-sm font-semibold text-gray-700 mb-3">
        {{ t('Inventory') }}
      </h4>
      <div class="space-y-4">
        <div class="flex items-center gap-3">
          <ToggleSwitch
            :model-value="modelValue.can_view_stock"
            input-id="can_view_stock"
            @update:model-value="update('can_view_stock', $event)"
          />
          <label
            for="can_view_stock"
            class="text-sm font-medium cursor-pointer"
          >
            {{ t('View stock levels') }}
          </label>
        </div>

        <div class="flex items-center gap-3">
          <ToggleSwitch
            :model-value="modelValue.can_see_stock_levels"
            input-id="can_see_stock_levels"
            @update:model-value="update('can_see_stock_levels', $event)"
          />
          <label
            for="can_see_stock_levels"
            class="text-sm font-medium cursor-pointer"
          >
            {{ t('See current stock levels') }}
          </label>
        </div>

        <div class="flex items-center gap-3">
          <ToggleSwitch
            :model-value="modelValue.can_update_stock"
            input-id="can_update_stock"
            @update:model-value="update('can_update_stock', $event)"
          />
          <label
            for="can_update_stock"
            class="text-sm font-medium cursor-pointer"
          >
            {{ t('Update stock') }}
          </label>
        </div>
      </div>
    </div>

    <div>
      <h4 class="text-sm font-semibold text-gray-700 mb-3">
        {{ t('POS Actions') }}
      </h4>
      <div class="space-y-4">
        <div class="flex items-center gap-3">
          <ToggleSwitch
            :model-value="modelValue.can_apply_discount"
            input-id="can_apply_discount"
            @update:model-value="update('can_apply_discount', $event)"
          />
          <label
            for="can_apply_discount"
            class="text-sm font-medium cursor-pointer"
          >
            {{ t('Apply discounts') }}
          </label>
        </div>

        <div class="flex items-center gap-3">
          <ToggleSwitch
            :model-value="modelValue.can_void_item"
            input-id="can_void_item"
            @update:model-value="update('can_void_item', $event)"
          />
          <label
            for="can_void_item"
            class="text-sm font-medium cursor-pointer"
          >
            {{ t('Void items') }}
          </label>
        </div>

        <div class="flex items-center gap-3">
          <ToggleSwitch
            :model-value="modelValue.can_process_refund"
            input-id="can_process_refund"
            @update:model-value="update('can_process_refund', $event)"
          />
          <label
            for="can_process_refund"
            class="text-sm font-medium cursor-pointer"
          >
            {{ t('Process refunds') }}
          </label>
        </div>
      </div>
    </div>

    <div>
      <h4 class="text-sm font-semibold text-gray-700 mb-3">
        {{ t('Administration') }}
      </h4>
      <div class="space-y-4">
        <div class="flex items-center gap-3">
          <ToggleSwitch
            :model-value="modelValue.can_manage_staff"
            input-id="can_manage_staff"
            @update:model-value="update('can_manage_staff', $event)"
          />
          <label
            for="can_manage_staff"
            class="text-sm font-medium cursor-pointer"
          >
            {{ t('Manage staff') }}
          </label>
        </div>
      </div>
    </div>
  </div>
</template>
